import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FormFields } from '@/shared/api/client';
import { saveBlob } from '@/shared/lib/saveBlob';
import { useSaveForm } from '@/shared/lib/useSaveForm';
import {
  EXPORT_STATUS,
  cancelExport,
  createExport,
  downloadExport,
  fetchLastExport,
  type ExportInfo,
} from './api';

const POLL_INTERVAL_MS = 5000;

const isRunning = (info: ExportInfo | null | undefined) =>
  info?.status_id === EXPORT_STATUS.PLANNED || info?.status_id === EXPORT_STATUS.IN_PROGRESS;

/** Last export of the `/export/<path>/` family: polling while it runs, create / cancel / download actions. */
export function useExportJob(path: string, downloadFileName: string) {
  const queryClient = useQueryClient();
  const queryKey = ['export', path];

  const lastExport = useQuery({
    queryKey,
    queryFn: () => fetchLastExport(path),
    refetchInterval: (query) => (isRunning(query.state.data) ? POLL_INTERVAL_MS : false),
  });

  const create = useSaveForm(
    (params: FormFields) => createExport(path, params),
    (info) => queryClient.setQueryData(queryKey, info),
  );

  const cancel = useMutation({
    mutationFn: (exportId: number | string) => cancelExport(path, exportId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const download = useMutation({
    mutationFn: () => downloadExport(path),
    onSuccess: (blob) => saveBlob(blob, downloadFileName),
  });

  return {
    info: lastExport.data ?? null,
    isLoading: lastExport.isLoading,
    create,
    cancel,
    download,
    isBusy: create.isPending || cancel.isPending || download.isPending,
  };
}
